/**
 * Hook to track article generation status in real-time via WebSocket
 *
 * Tracks articles being generated for a specific announcement and provides
 * live updates on generation progress (generating → fact-checking → polishing → complete)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from './useWebSocket.js';

export interface ArticleGenerationState {
  articleId: string | null; // null until created
  style: string;
  provider: string;
  phase: 'generating' | 'fact_checking' | 'polishing' | 'language_review' | 'completed';
  status: string;
  step?: string;
  claimsVerified?: number;
  startTime: number;
  completedTime?: number;
  metadata?: any;
}

interface UseArticleGenerationOptions {
  announcementId: string;
  tenantId?: string;
  enabled?: boolean;
}

export function useArticleGeneration({ announcementId, tenantId, enabled = true }: UseArticleGenerationOptions) {
  const [generatingArticles, setGeneratingArticles] = useState<Map<string, ArticleGenerationState>>(
    new Map()
  );
  const [completedArticleIds, setCompletedArticleIds] = useState<string[]>([]);

  // Track regeneration requests to create initial state
  const pendingGenerations = useRef<Map<string, { style: string; provider: string }>>(new Map());

  // Connect to WebSocket for this announcement
  // Channel matches what the backend emits via event_emitter.py:
  //   "assayerai:announcement:{announcement_id}"
  // V2 has no tenant isolation — all articles are system-level (customer_id = NULL).
  // tenantId param is retained for API compatibility but not used for channel routing.
  const wsChannels = enabled
    ? [`assayerai:announcement:${announcementId}`]
    : [];

  const { connected } = useWebSocket({
    channels: wsChannels,
    onEvent: {
      status_change: (data) => {
        console.log('📡 Article Generation: Status change:', data);
        handleStatusChange(data);
      },
      article_created: (data) => {
        console.log('📡 Article Generation: Article created:', data);
        handleArticleCreated(data);
      },
    },
    autoInvalidate: false, // We manage state manually
    debug: true,
  });

  const handleStatusChange = useCallback((data: any) => {
    const { status, step, metadata, announcement_id } = data;

    // Only process events for this announcement
    if (announcement_id !== announcementId) return;

    // Map status to phase
    let phase: ArticleGenerationState['phase'] = 'generating';
    if (status === 'fact_checking') phase = 'fact_checking';
    else if (status === 'polishing') phase = 'polishing';
    else if (status === 'language_review') phase = 'language_review';
    else if (status === 'completed') phase = 'completed';

    // Create or update generation state
    // Use a tracking key based on style (since we don't have article_id yet)
    const style = metadata?.style || metadata?.article_title || 'Unknown';
    const provider = metadata?.provider || 'anthropic';
    const trackingKey = `${announcementId}-${style}`;

    setGeneratingArticles((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(trackingKey);

      if (existing) {
        // Update existing state
        newMap.set(trackingKey, {
          ...existing,
          phase,
          status,
          step,
          claimsVerified: metadata?.claims_verified || existing.claimsVerified,
          metadata,
        });
      } else if (status === 'regenerating' || status === 'generating') {
        // Create new tracking entry for article generation
        newMap.set(trackingKey, {
          articleId: null,
          style,
          provider,
          phase,
          status,
          step,
          startTime: Date.now(),
          metadata,
        });
      }

      return newMap;
    });
  }, [announcementId]);

  const handleArticleCreated = useCallback((data: any) => {
    const { article_id, style, announcement_id } = data;

    // Only process events for this announcement
    if (announcement_id !== announcementId) return;

    const trackingKey = `${announcementId}-${style}`;

    setGeneratingArticles((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(trackingKey);

      if (existing) {
        // Mark as completed and set article ID
        newMap.set(trackingKey, {
          ...existing,
          articleId: article_id,
          phase: 'completed',
          status: 'completed',
          completedTime: Date.now(),
        });

        // Move to completed list after a brief delay
        setTimeout(() => {
          setCompletedArticleIds((prev) => [...prev, article_id]);
          setGeneratingArticles((current) => {
            const updated = new Map(current);
            updated.delete(trackingKey);
            return updated;
          });
        }, 3000); // Show success state for 3 seconds
      }

      return newMap;
    });
  }, [announcementId]);

  // Method to start tracking a new article generation
  const startGenerating = useCallback((style: string, provider: string) => {
    const trackingKey = `${announcementId}-${style}`;

    setGeneratingArticles((prev) => {
      const newMap = new Map(prev);
      newMap.set(trackingKey, {
        articleId: null,
        style,
        provider,
        phase: 'generating',
        status: 'regenerating',
        startTime: Date.now(),
      });
      return newMap;
    });
  }, [announcementId]);

  return {
    generatingArticles: Array.from(generatingArticles.values()),
    completedArticleIds,
    connected,
    startGenerating,
  };
}
